import { logger } from '../utils/logger';
import React, { useEffect, useRef, useState } from 'react';
import { makeAuthenticatedRequest } from '../utils/api';
import { useNotifications } from '../contexts/NotificationContext';
import './WeeklyChallengePanel.css';

interface WeeklyChallenge {
  id: number;
  name: string;
  description: string;
  icon: string;
  objectiveType: string;
  progress: number;
  targetCount: number;
  rewardMaterialName: string | null;
  rewardMaterialQuantity: number;
  rewardGold: number;
  completed: boolean;
  claimed: boolean;
}

interface WeeklyResponse {
  challenges: WeeklyChallenge[];
}

const WeeklyChallengePanel: React.FC = () => {
  const [data, setData] = useState<WeeklyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const { addNotification } = useNotifications();
  const completedIdsRef = useRef<Set<number>>(new Set());
  const firstLoadRef = useRef(true);

  useEffect(() => {
    loadWeekly();
  }, []);

  const loadWeekly = async () => {
    try {
      const response = await makeAuthenticatedRequest('/weekly');
      if (response.ok) {
        const result: WeeklyResponse = await response.json();

        if (!firstLoadRef.current) {
          for (const challenge of result.challenges) {
            if (challenge.completed && !completedIdsRef.current.has(challenge.id)) {
              addNotification({
                type: 'quest',
                title: 'Weekly Challenge Complete!',
                message: `${challenge.name} - claim your reward on the Home screen`,
                icon: challenge.icon
              });
            }
          }
        }
        firstLoadRef.current = false;
        completedIdsRef.current = new Set(result.challenges.filter(c => c.completed).map(c => c.id));

        setData(result);
      }
    } catch (err) {
      logger.error('Failed to load weekly challenges:', err);
    } finally {
      setLoading(false);
    }
  };

  const claimReward = async (challengeId: number) => {
    setClaimingId(challengeId);
    try {
      const response = await makeAuthenticatedRequest(`/weekly/claim/${challengeId}`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        const rewardParts = [];
        if (result.materialAwarded) rewardParts.push(`${result.materialQuantityAwarded}x ${result.materialAwarded}`);
        if (result.goldAwarded) rewardParts.push(`${result.goldAwarded} gold`);
        addNotification({
          type: 'loot',
          title: 'Reward Claimed!',
          message: `+${rewardParts.join(', +')}`,
          icon: '🎁'
        });
        await loadWeekly();
      } else {
        const err = await response.json().catch(() => ({ message: 'Failed to claim reward' }));
        addNotification({ type: 'info', title: 'Could not claim', message: err.message || 'Failed to claim reward' });
      }
    } catch (err) {
      logger.error('Failed to claim weekly challenge reward:', err);
    } finally {
      setClaimingId(null);
    }
  };

  if (loading) {
    return (
      <div className="weekly-challenge-panel">
        <h3>Weekly Challenges</h3>
        <p>Loading...</p>
      </div>
    );
  }

  if (!data || data.challenges.length === 0) return null;

  return (
    <div className="weekly-challenge-panel">
      <h3>Weekly Challenges</h3>

      <div className="weekly-challenge-list">
        {data.challenges.map(challenge => {
          const pct = Math.min(100, Math.round((challenge.progress / challenge.targetCount) * 100));
          return (
            <div key={challenge.id} className={`weekly-challenge-item ${challenge.claimed ? 'claimed' : challenge.completed ? 'ready' : ''}`}>
              <span className="weekly-challenge-icon">{challenge.icon}</span>
              <div className="weekly-challenge-info">
                <div className="weekly-challenge-name">{challenge.name}</div>
                <div className="weekly-challenge-desc">{challenge.description}</div>
                <div className="weekly-challenge-bar-track">
                  <div className="weekly-challenge-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="weekly-challenge-meta">
                  <span>{challenge.progress} / {challenge.targetCount}</span>
                  <span className="weekly-challenge-reward">
                    {challenge.rewardMaterialName ? `${challenge.rewardMaterialQuantity}x ${challenge.rewardMaterialName}` : ''}
                    {challenge.rewardMaterialName && challenge.rewardGold > 0 ? ' + ' : ''}
                    {challenge.rewardGold > 0 ? `💰 ${challenge.rewardGold}` : ''}
                  </span>
                </div>
              </div>
              {challenge.claimed ? (
                <span className="weekly-challenge-claimed-badge">Claimed</span>
              ) : challenge.completed ? (
                <button
                  className="weekly-challenge-claim-btn"
                  disabled={claimingId === challenge.id}
                  onClick={() => claimReward(challenge.id)}
                >
                  {claimingId === challenge.id ? '...' : 'Claim'}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyChallengePanel;
